import * as THREE from 'three';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {RoomPlannerProductWithTemplate} from 'components/customer/RoomPlanner/types';
import {
    isAppliedPanelWithDepth,
    isAppliedPanelWithWidth,
    isAppliedPanelWithLength,
    isManualPanel,
} from './panelTypeHelpers';

interface CabinetDepthParams {
    cabinetGroup: THREE.Group;
    values: RoomPlannerProductWithTemplate;
    variables: TemplateVariable;
    templateName: string;
    carcThickness: number;
    isLShapedFootprint: boolean;
    isFloorProduct: boolean;
    hasPairOfDoorsWithInnerDrawer: boolean;
    isRobeProduct: boolean;
    noBottom: boolean;
    isPairDoor: boolean | undefined;
}

/**
 * Calculates the default cabinet depth
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @param {boolean} hasPairOfDoorsWithInnerDrawer - Whether cabinet has pair doors with inner drawer
 * @return {number} Calculated depth in scaled units
 */
const calculateDefaultDepth = (
    values: RoomPlannerProductWithTemplate,
    hasPairOfDoorsWithInnerDrawer: boolean
): number => {
    if (!values.cabinet_depth) {
        return 0;
    }

    const baseDepth = Number(values.cabinet_depth);
    const additionalDepth = hasPairOfDoorsWithInnerDrawer
        ? Number(values.cabinet_carc_thickness) + 2
        : 0;

    return (baseDepth + additionalDepth) * 0.2;
};

/**
 * Calculates depth for applied panels with depth
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @return {number} Calculated depth in scaled units
 */
const calculateAppliedPanelDepth = (
    values: RoomPlannerProductWithTemplate
): number => {
    return values.cabinet_applied_panel_depth
        ? Number(values.cabinet_applied_panel_depth) * 0.2
        : 0;
};

/**
 * Calculates depth for manual panels (flat panels)
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @param {string} templateName - Template name
 * @param {number} carcThickness - Carcase thickness
 * @return {number} Calculated depth in scaled units
 */
const calculateManualPanelDepth = (
    values: RoomPlannerProductWithTemplate,
    templateName: string,
    carcThickness: number
): number => {
    const isExteriorPanel = templateName.includes('Exterior');
    const panelThickness = isExteriorPanel
        ? values.cabinet_ext_thickness
        : carcThickness;

    return panelThickness ? Number(panelThickness) * 0.2 : 0;
};

/**
 * Calculates depth for applied panels with width
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @return {number} Calculated depth in scaled units
 */
const calculateAppliedPanelWithWidthDepth = (
    values: RoomPlannerProductWithTemplate
): number => {
    return values.cabinet_panel_width
        ? Number(values.cabinet_panel_width) * 0.2
        : 0;
};

/**
 * Calculates depth for applied panels with length
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @return {number} Calculated depth in scaled units
 */
const calculateAppliedPanelWithLengthDepth = (
    values: RoomPlannerProductWithTemplate
): number => {
    return values.cabinet_panel_length
        ? Number(values.cabinet_panel_length) * 0.2
        : 0;
};

/**
 * Calculates depth for U-shaped panels
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @return {number} Calculated depth in scaled units
 */
const calculateUShapedPanelDepth = (
    values: RoomPlannerProductWithTemplate
): number => {
    return values.cabinet_panel_width
        ? Number(values.cabinet_panel_width) * 0.2
        : 0;
};

/**
 * Calculates depth for floor products (kicker framed)
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @return {number} Calculated depth in scaled units
 */
const calculateFloorProductDepth = (
    values: RoomPlannerProductWithTemplate
): number => {
    const extThickness = values.cabinet_ext_thickness ?? 0;
    let floorProductDepth = values.cabinet_depth
        ? Number(values.cabinet_depth - extThickness * 2) * 0.2
        : 0;

    if (!values.cabinet_edge_L2) {
        floorProductDepth += extThickness * 0.2;
    }

    return floorProductDepth;
};

/**
 * Calculates depth for L-shaped cabinets
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @param {number} carcThickness - Carcase thickness
 * @return {number} Calculated depth in scaled units
 */
const calculateLShapedDepth = (
    values: RoomPlannerProductWithTemplate,
    carcThickness: number
): number => {
    return values.cabinet_left_width !== undefined
        ? (Number(values.cabinet_left_width) + Number(carcThickness)) * 0.2
        : 0;
};

/**
 * Assigns cabinet group user data including depth calculations and basic properties
 */
export const assignCabinetGroupUserData = ({
    cabinetGroup,
    values,
    variables,
    templateName,
    carcThickness,
    isLShapedFootprint,
    isFloorProduct,
    hasPairOfDoorsWithInnerDrawer,
    isRobeProduct,
    noBottom,
    isPairDoor,
}: CabinetDepthParams): void => {
    // Set cabinet name
    cabinetGroup.name = `cabinet-group-${values.id}`;

    // Set basic user data properties
    cabinetGroup.userData.isRobeProduct = isRobeProduct;
    cabinetGroup.userData.noBottom = noBottom;
    cabinetGroup.userData.hasRightReturnRightHangDoor =
        variables.isReturnProduct && !isPairDoor;

    // Default depth calculation
    cabinetGroup.userData.depth = calculateDefaultDepth(
        values,
        hasPairOfDoorsWithInnerDrawer
    );

    // Applied panel with depth
    if (isAppliedPanelWithDepth(templateName)) {
        cabinetGroup.userData.depth = calculateAppliedPanelDepth(values);
        return;
    }

    // Applied panel with width (including manual panels)
    if (isAppliedPanelWithWidth(templateName)) {
        if (isManualPanel(templateName, variables)) {
            cabinetGroup.userData.depth = calculateManualPanelDepth(
                values,
                templateName,
                carcThickness
            );
        } else {
            cabinetGroup.userData.depth =
                calculateAppliedPanelWithWidthDepth(values);
        }
        return;
    }

    // Applied panel with length
    if (isAppliedPanelWithLength(templateName)) {
        cabinetGroup.userData.depth =
            calculateAppliedPanelWithLengthDepth(values);
        return;
    }

    // U-shaped panel
    if (variables.isUShapedPanel) {
        cabinetGroup.userData.depth = calculateUShapedPanelDepth(values);
        return;
    }

    // Floor product (kicker framed)
    if (isFloorProduct) {
        cabinetGroup.userData.depth = calculateFloorProductDepth(values);
        return;
    }

    // L-shaped footprint
    if (isLShapedFootprint) {
        cabinetGroup.userData.depth = calculateLShapedDepth(
            values,
            carcThickness
        );
    }
};
